import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

const TOOLTIP_MAX_WIDTH = 320;
const GAP = 6;
const EDGE = 8;
// Long enough to cross GAP from the badge to the tooltip without it closing under the pointer.
const HOVER_CLOSE_DELAY_MS = 150;

// One reason on screen at a time: badges sit closer together than a tooltip is wide.
let closeOpenTooltip: (() => void) | null = null;

type ReasonBadgeProps = {
  label: React.ReactNode;
  /** Why the badge is shown. Without it the badge is a plain, non-focusable pill. */
  reason?: string;
  /** The pill's look — each place keeps its own. */
  className: string;
};

/**
 * A pill whose reason opens on hover, keyboard focus and tap, and is read by screen readers with
 * the badge (`aria-describedby`). With a reason the pill is a `<button>`, so it must not sit inside
 * another button or link. In running text a button is inline-block, so unlike a `span` its vertical
 * padding adds to the line: give it a line-height that keeps padding + line-height within the line.
 */
export const ReasonBadge: React.FC<ReasonBadgeProps> = ({ label, reason, className }) => {
  const id = useId();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  // Focus and click both pin it: iOS Safari does not focus a button on tap, so a tap only clicks.
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const open = hovered || pinned;
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>();

  const enter = (): void => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
  };
  const leave = (): void => {
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setHovered(false), HOVER_CLOSE_DELAY_MS);
  };
  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const place = useCallback((): void => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const height = tipRef.current?.offsetHeight ?? 0;
    const width = tipRef.current?.offsetWidth || TOOLTIP_MAX_WIDTH;
    const left = Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE));
    const below = rect.bottom + GAP;
    const above = rect.top - GAP - height;
    const flip = below + height > window.innerHeight - EDGE && above >= EDGE;
    setPos({ top: flip ? above : below, left });
  }, []);

  // Placed once the tooltip is shown: a hidden tooltip has no size to place by.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const close = (): void => {
      clearTimeout(leaveTimer.current);
      setPinned(false);
      setHovered(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      // A Headless UI Dialog closes on an Escape that reaches `window` un-prevented, so the
      // first Escape closes only the tooltip, not the drawer around it.
      e.preventDefault();
      close();
    };
    // A press on the tooltip itself keeps it (to select the reason); it also blurs the badge, but
    // the pointer over the tooltip keeps it hovered.
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (!anchorRef.current?.contains(target) && !tipRef.current?.contains(target)) close();
    };
    // Follow the badge rather than close: focusing a badge in a scrolled bar scrolls it into view,
    // which would otherwise close the tooltip the same focus just opened. Close only once the
    // badge has left the area that scrolled.
    const onScroll = (e: Event): void => {
      const anchor = anchorRef.current;
      if (anchor && e.target instanceof Element && e.target.contains(anchor)) {
        const area = e.target.getBoundingClientRect();
        const r = anchor.getBoundingClientRect();
        const outside =
          r.bottom <= area.top || r.top >= area.bottom || r.right <= area.left || r.left >= area.right;
        if (outside) {
          close();
          return;
        }
      }
      place();
    };
    closeOpenTooltip?.();
    closeOpenTooltip = close;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      if (closeOpenTooltip === close) closeOpenTooltip = null;
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, place]);

  if (!reason) return <span className={className}>{label}</span>;

  const pin = (): void => setPinned(true);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-describedby={id}
        className={clsx(
          className,
          'cursor-help focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500'
        )}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onFocus={pin}
        onBlur={() => setPinned(false)}
        onClick={pin}
      >
        {label}
      </button>
      {/* Always mounted so `aria-describedby` resolves while the tooltip is closed. */}
      {createPortal(
        <div
          ref={tipRef}
          id={id}
          role="tooltip"
          hidden={!open}
          className="fixed z-[10002] w-max rounded-md bg-gray-900 px-3 py-2 text-xs font-normal normal-case tracking-normal text-white shadow-lg"
          style={{ top: pos.top, left: pos.left, maxWidth: TOOLTIP_MAX_WIDTH }}
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          {reason}
        </div>,
        document.body
      )}
    </>
  );
};
